import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { organizationApi } from "@/lib/api/organization";
import type {
  JoinRequest,
  OrganizationDomain,
  OrganizationMember,
  OrganizationTeam,
} from "@/lib/api/organization";
import type { OrgRole } from "@mycompbox/shared";

export const organizationKeys = {
  all: ["organization"] as const,
  members: () => [...organizationKeys.all, "members"] as const,
  domains: () => [...organizationKeys.all, "domains"] as const,
  details: () => [...organizationKeys.all, "details"] as const,
  joinRequests: () => [...organizationKeys.all, "join-requests"] as const,
  teams: () => [...organizationKeys.all, "teams"] as const,
};

/** The organization's teams (org admins only). */
export function useOrganizationTeams() {
  return useQuery({
    queryKey: organizationKeys.teams(),
    queryFn: () => organizationApi.teams(),
    select: (response): OrganizationTeam[] => response.data,
  });
}

/**
 * Add a member to a team, or take them off one. Refetches the members list
 * (which carries each member's teams) and every team query.
 */
export function useMemberTeamActions() {
  const queryClient = useQueryClient();
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: organizationKeys.members() });
    await queryClient.invalidateQueries({ queryKey: ["teams"] });
  };
  return {
    add: useMutation({
      mutationFn: ({ memberId, teamId }: { memberId: string; teamId: string }) =>
        organizationApi.addMemberToTeam(memberId, teamId),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: ({ memberId, teamId }: { memberId: string; teamId: string }) =>
        organizationApi.removeMemberFromTeam(memberId, teamId),
      onSuccess: refresh,
    }),
  };
}

/**
 * The members of the caller's organization.
 *
 * Admin-only on the server; for a plain member this query fails with 403 and
 * the members screen never mounts it (the route is gated), so no retry
 * special-casing is needed here.
 */
export function useMembers() {
  return useQuery({
    queryKey: organizationKeys.members(),
    queryFn: () => organizationApi.members(),
    select: (response): OrganizationMember[] => response.data,
  });
}

/**
 * Change a member's organization role.
 *
 * Invalidates the members list on success so the table reflects the new role
 * immediately — the server is the source of truth for the row, so refetching
 * beats patching the cache by hand (which would miss any concurrent change
 * another admin made).
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: OrgRole }) =>
      organizationApi.updateMemberRole(id, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationKeys.members(),
      });
    },
  });
}

/**
 * Remove a member from the organization.
 *
 * Same invalidation rationale as the role change. Errors (self-removal,
 * last-admin, member of another org) carry the server's message and are shown
 * verbatim by the caller.
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => organizationApi.removeMember(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationKeys.members(),
      });
    },
  });
}

/** Claimed company email domains (org-admin only on the server). */
export function useDomains() {
  return useQuery({
    queryKey: organizationKeys.domains(),
    queryFn: () => organizationApi.domains(),
    select: (response): OrganizationDomain[] => response.data,
  });
}

/** Domain mutations (claim / verify / toggle / remove), each refetching the list. */
export function useDomainActions() {
  const queryClient = useQueryClient();
  const onSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: organizationKeys.domains() });
  };
  return {
    claim: useMutation({ mutationFn: (domain: string) => organizationApi.claimDomain(domain), onSuccess }),
    verify: useMutation({ mutationFn: (id: string) => organizationApi.verifyDomain(id), onSuccess }),
    setAutoJoin: useMutation({
      mutationFn: ({ id, autoJoin }: { id: string; autoJoin: boolean }) =>
        organizationApi.setDomainAutoJoin(id, autoJoin),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: (id: string) => organizationApi.removeDomain(id), onSuccess }),
  };
}

/** The caller's organization (org-admin only on the server). */
export function useOrganization() {
  return useQuery({
    queryKey: organizationKeys.details(),
    queryFn: () => organizationApi.get(),
    select: (response) => response.data,
  });
}

/** Rename the organization; refreshes it and the onboarding state. */
export function useRenameOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => organizationApi.rename(name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationKeys.details() });
      void queryClient.invalidateQueries({ queryKey: ["onboarding"] });
    },
  });
}

/** Pending requests to join the organization (org-admin only on the server). */
export function useJoinRequests() {
  return useQuery({
    queryKey: organizationKeys.joinRequests(),
    queryFn: () => organizationApi.joinRequests(),
    select: (response): JoinRequest[] => response.data,
  });
}

/** Approve / decline a join request; refreshes requests and members. */
export function useJoinRequestActions() {
  const queryClient = useQueryClient();
  const onSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: organizationKeys.joinRequests() });
    void queryClient.invalidateQueries({ queryKey: organizationKeys.members() });
  };
  return {
    approve: useMutation({ mutationFn: (id: string) => organizationApi.approveJoinRequest(id), onSuccess }),
    decline: useMutation({ mutationFn: (id: string) => organizationApi.declineJoinRequest(id), onSuccess }),
  };
}
